import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { AppDialog, AppDialogContent, AppDialogActions, AppDialogTitle } from '@/UI/AppDialog/AppDialog';
import { Box } from '@/UI/Box/Box';
import { TextStdRg, TextLgSb } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { AppCheckbox } from '@/UI/AppCheckbox/AppCheckbox';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { AsteriskIcon } from '@/UI/Icons';
import { useLocalStorage } from '@/hooks/useLocalStorage';

interface ExpensiveRunModalProps {
  onConfirm: () => void;
  onClose: () => void;
  open?: boolean;
  cost: number;
}

export const ExpensiveRunModal = ({ onClose, onConfirm, open = false, cost }: ExpensiveRunModalProps) => {
  const { t } = useTranslation();

  const { getItem, setItem } = useLocalStorage<boolean>('allowRunExpensiveRun');
  const [checked, setChecked] = useState(getItem() ?? false);

  useEffect(() => {
    if (open) {
      setChecked(getItem() ?? false);
    }
  }, [open, getItem]);

  return (
    <AppDialog
      data-testid="expensive-run-warning-modal"
      open={open}
      onClose={onClose}
      aria-labelledby="expensive-run-warning-modal"
      slotProps={{
        paper: {
          sx: { background: color.Black92, width: 450 },
        },
      }}
    >
      <AppDialogTitle sx={{ display: 'flex', justifyContent: 'flex-end', pb: 1 }}>
        <AppXBtn onClick={onClose} size={20} />
      </AppDialogTitle>
      <AppDialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextLgSb>{t(I18N_KEYS.COMMON_COMPONENTS.EXPENSIVE_RUN_MODAL.TITLE)}</TextLgSb>
        <TextStdRg color={color.White100} sx={{ whiteSpace: 'pre-line' }}>
          <Trans
            i18nKey={I18N_KEYS.COMMON_COMPONENTS.EXPENSIVE_RUN_MODAL.SUBTITLE}
            components={{
              b: <b />,
              icon: <AsteriskIcon width={14} height={14} style={{ verticalAlign: 'sub' }} />,
            }}
            values={{
              cost,
            }}
          />
        </TextStdRg>
        <Box sx={{ pt: 1 }}>
          <AppCheckbox
            checked={checked}
            onChange={(_, newChecked) => setChecked(newChecked)}
            label={t(I18N_KEYS.COMMON_COMPONENTS.EXPENSIVE_RUN_MODAL.CHECKBOX_LABEL)}
          />
        </Box>
      </AppDialogContent>
      <AppDialogActions sx={{ px: 3, pb: 3 }}>
        <ButtonContained mode="text" onClick={onClose} size="medium">
          {t(I18N_KEYS.RECIPE_MAIN.FLOW.PRICY_MODEL_MODAL.CTA_CANCEL)}
        </ButtonContained>
        <ButtonContained
          mode="filled-light"
          onClick={() => {
            setItem(checked);
            onConfirm();
          }}
          size="medium"
        >
          {t(I18N_KEYS.RECIPE_MAIN.FLOW.PRICY_MODEL_MODAL.CTA_CONFIRM)}
        </ButtonContained>
      </AppDialogActions>
    </AppDialog>
  );
};
